import React, { useEffect, useState } from "react";
import CloseModal from "../../../components/Atomos/Icons/EditMode/CloseModal";

export type ImageLightboxModalProps = {
  isOpen: boolean;
  imageSrc: string | null;
  onClose: () => void;
  currentIndex?: number;
  totalCount?: number;
  onPrevious?: () => void;
  onNext?: () => void;
};

export default function ImageLightboxModal({
  isOpen,
  imageSrc,
  onClose,
  currentIndex = 0,
  totalCount = 0,
  onPrevious,
  onNext,
}: ImageLightboxModalProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
      document.body.style.overflow = "";
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "ArrowLeft") {
        onPrevious?.();
      }
      if (e.key === "ArrowRight") {
        onNext?.();
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [isOpen, onClose, onPrevious, onNext]);

  if (!isOpen) {
    return null;
  }

  const showIndex =
    typeof currentIndex === "number" &&
    typeof totalCount === "number" &&
    totalCount > 0;

  return (
    <div
      className={`fixed inset-0 top-20 z-[1000] flex items-center justify-center bg-black/80 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="View photo"
    >
      <div
        className="relative flex items-center justify-center max-w-[80vw] max-h-[80vh] w-fit h-fit z-[1001] pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        {totalCount > 1 && onPrevious && currentIndex > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 -translate-x-0 w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-[1002] pointer-events-auto"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        {totalCount > 1 && onNext && currentIndex < totalCount - 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 translate-x-0 w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-[1002] pointer-events-auto"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
        {imageSrc && (
          <>
            <img
              src={imageSrc}
              alt="Photos Event"
              className={`max-w-[80vw] max-h-[80vh] rounded-lg w-auto h-auto object-contain pointer-events-auto transition-all duration-300 ease-out ${visible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="absolute top-3 right-3 p-1 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-[1002] pointer-events-auto [&_svg]:fill-white"
              aria-label="Close"
            >
              <CloseModal />
            </button>
            {showIndex && (
              <div
                className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded bg-black/50 text-white text-sm font-medium pointer-events-none z-[1002]"
                aria-live="polite"
              >
                {currentIndex + 1} / {totalCount}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
